import React, { useRef, useEffect } from 'react';
import { ChevronRight, Network, Shield, AlertTriangle, Maximize2, Download, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/Badge';
import { ForceDirectedGraph } from '../components/graphs/ForceDirectedGraph';

// Sample graph data
const graphData = {
  metrics: {
    totalAssets: 342,
    exposedAssets: 89,
    criticalAssets: 24,
    attackPaths: 156
  },
  nodes: [
    { id: 'domain1', label: 'example.com', type: 'domain', risk: 'high' },
    { id: 'subdomain1', label: 'api.example.com', type: 'subdomain', risk: 'critical' },
    { id: 'subdomain2', label: 'admin.example.com', type: 'subdomain', risk: 'medium' },
    { id: 'ip1', label: '192.168.1.100', type: 'ip', risk: 'high' },
    { id: 'ip2', label: '192.168.1.101', type: 'ip', risk: 'low' },
    { id: 'port1', label: 'Port 443', type: 'port', risk: 'medium' },
    { id: 'port2', label: 'Port 80', type: 'port', risk: 'low' },
    { id: 'vuln1', label: 'SQL Injection', type: 'vulnerability', risk: 'critical' },
    { id: 'vuln2', label: 'XSS', type: 'vulnerability', risk: 'high' }
  ],
  edges: [
    { source: 'domain1', target: 'subdomain1', type: 'contains' },
    { source: 'domain1', target: 'subdomain2', type: 'contains' },
    { source: 'subdomain1', target: 'ip1', type: 'resolves_to' },
    { source: 'subdomain2', target: 'ip2', type: 'resolves_to' },
    { source: 'ip1', target: 'port1', type: 'exposes' },
    { source: 'ip2', target: 'port2', type: 'exposes' },
    { source: 'port1', target: 'vuln1', type: 'vulnerable_to' },
    { source: 'port1', target: 'vuln2', type: 'vulnerable_to' }
  ],
  attackPaths: [
    {
      id: '1',
      severity: 'critical',
      path: ['domain1', 'subdomain1', 'ip1', 'port1', 'vuln1'],
      description: 'Critical SQL injection vulnerability accessible through public endpoint'
    },
    {
      id: '2',
      severity: 'high',
      path: ['domain1', 'subdomain1', 'ip1', 'port1', 'vuln2'],
      description: 'Cross-site scripting vulnerability in admin interface'
    }
  ]
};

const AttackSurfaceGraph = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    // In a real implementation, you would use a graph visualization library like:
    // - Cytoscape.js for complex graph visualizations
    // - D3.js for custom graph rendering
    // - VivaGraphJS for large-scale graphs
    // - Sigma.js for interactive graph visualization
    
    // This is a placeholder to show where the graph would be rendered
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvasRef.current.width = canvasRef.current.offsetWidth;
    canvasRef.current.height = canvasRef.current.offsetHeight;

    // Clear canvas
    ctx.fillStyle = '#0F1629';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    // Draw placeholder text
    ctx.fillStyle = '#4B5563';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Attack Surface Graph Visualization', canvasRef.current.width / 2, canvasRef.current.height / 2);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Network className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Attack Surface Graph</h1>
            <p className="text-sm text-gray-400">Visualize and analyze your attack surface</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Total Assets</span>
            <Network className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-2xl font-bold text-white">{graphData.metrics.totalAssets}</div>
        </div>
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Exposed Assets</span>
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="text-2xl font-bold text-white">{graphData.metrics.exposedAssets}</div>
        </div>
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Critical Assets</span>
            <Shield className="h-5 w-5 text-red-500" />
          </div>
          <div className="text-2xl font-bold text-white">{graphData.metrics.criticalAssets}</div>
        </div>
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Attack Paths</span>
            <Network className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-2xl font-bold text-white">{graphData.metrics.attackPaths}</div>
        </div>
      </div>

      {/* Graph and Attack Paths */}
      <div className="grid grid-cols-3 gap-6">
        {/* Graph Visualization - Takes up 2 columns */}
        <div className="col-span-2 bg-[#0F1629] rounded-xl border border-gray-800/50">
          {/* Graph Controls */}
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button variant="outline" size="sm">
                  <Maximize2 className="h-4 w-4 mr-2" />
                  Zoom to Fit
                </Button>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-400">Layout:</label>
                  <select className="bg-gray-800/30 border border-gray-800 rounded-lg text-sm text-white px-2 py-1">
                    <option>Force-Directed</option>
                    <option>Hierarchical</option>
                    <option>Circular</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="h-3 w-3 rounded-full bg-red-500" />
                  <span className="text-xs text-gray-400">Critical</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="h-3 w-3 rounded-full bg-orange-500" />
                  <span className="text-xs text-gray-400">High</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="h-3 w-3 rounded-full bg-yellow-500" />
                  <span className="text-xs text-gray-400">Medium</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="h-3 w-3 rounded-full bg-emerald-500" />
                  <span className="text-xs text-gray-400">Low</span>
                </div>
              </div>
            </div>
          </div>

          {/* Graph Canvas */}
          <div className="p-6">
            <div className="w-full h-[600px] rounded-lg bg-[#0F1629]">
              <ForceDirectedGraph
                nodes={graphData.nodes}
                links={graphData.edges}
                width={800}
                height={600}
              />
            </div>
          </div>
        </div>

        {/* Attack Paths - Takes up 1 column */}
        <div className="space-y-6">
          {/* Attack Paths */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Attack Paths</h2>
            <div className="space-y-4">
              {graphData.attackPaths.map((path) => (
                <div key={path.id} className="p-4 bg-gray-800/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={path.severity as "default" | "low" | "medium" | "high" | "critical" | "A" | "B" | "C" | "F" | "info" | "secondary" | "destructive" | "outline" | "success" | "warning" | "active" | "maintenance"}>{path.severity}</Badge>
                    <Button variant="outline" size="sm">View Details</Button>
                  </div>
                  <p className="text-sm text-gray-300 mb-3">{path.description}</p>
                  <div className="flex items-center space-x-2">
                    {path.path.map((node, index) => (
                      <React.Fragment key={node}>
                        <span className="text-xs text-gray-400">{node}</span>
                        {index < path.path.length - 1 && (
                          <ChevronRight className="h-3 w-3 text-gray-600" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Graph Statistics */}
          <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <h2 className="text-lg font-semibold text-white mb-4">Graph Statistics</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                <span className="text-sm text-gray-400">Total Nodes</span>
                <span className="text-sm text-white">{graphData.nodes.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                <span className="text-sm text-gray-400">Total Edges</span>
                <span className="text-sm text-white">{graphData.edges.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                <span className="text-sm text-gray-400">Average Degree</span>
                <span className="text-sm text-white">
                  {(graphData.edges.length * 2 / graphData.nodes.length).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttackSurfaceGraph;